import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchCurrentUser, loginUser, setAuthToken, signupUser, verifyEmail as verifyEmailApi, resendOtp as resendOtpApi, googleLogin as googleLoginApi, appleLogin as appleLoginApi } from "../services/api";

const STORAGE_KEY = "heliocentric_auth";

const getStoredAuth = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { user: null, token: null };
    }

    const parsed = JSON.parse(raw);
    return {
      user: parsed.user || null,
      token: parsed.token || null,
    };
  } catch (_error) {
    return { user: null, token: null };
  }
};

const readStoredAuth = () => {
  const auth = getStoredAuth();
  if (auth.token) {
    setAuthToken(auth.token);
  }
  return auth;
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const storedAuth = useMemo(() => readStoredAuth(), []);
  const [user, setUser] = useState(storedAuth.user);
  const [token, setToken] = useState(storedAuth.token);
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  const persistAuth = useCallback((nextUser, nextToken) => {
    setUser(nextUser);
    setToken(nextToken);
    if (nextToken) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ user: nextUser, token: nextToken }));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const clearAuth = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem(STORAGE_KEY);
    setAuthToken(null);
  }, []);

  useEffect(() => {
    setAuthToken(token);
  }, [token]);

  useEffect(() => {
    let isMounted = true;

    const bootstrap = async () => {
      if (!token) {
        setInitialized(true);
        return;
      }

      try {
        setAuthLoading(true);
        const { user: profile } = await fetchCurrentUser();
        if (isMounted) {
          persistAuth(profile, token);
        }
      } catch (_error) {
        if (isMounted) {
          clearAuth();
        }
      } finally {
        if (isMounted) {
          setAuthLoading(false);
          setInitialized(true);
        }
      }
    };

    bootstrap();

    return () => {
      isMounted = false;
    };
  }, [token, persistAuth, clearAuth]);

  const handleAuthRequest = useCallback(async (requestFn) => {
    setAuthError(null);
    setAuthLoading(true);
    try {
      const response = await requestFn();
      if (response?.token) {
        setAuthToken(response.token);
      }
      persistAuth(response.user, response.token);
      return response.user;
    } catch (error) {
      const message = error?.message || "Something went wrong. Please try again.";
      setAuthError(message);
      throw error;
    } finally {
      setAuthLoading(false);
    }
  }, [persistAuth]);

  const login = useCallback(
    async (credentials) => {
      return handleAuthRequest(() => loginUser(credentials));
    },
    [handleAuthRequest]
  );

  const loginWithGoogle = useCallback(
    async (payload) => {
      return handleAuthRequest(() => googleLoginApi(payload));
    },
    [handleAuthRequest]
  );

  const loginWithApple = useCallback(
    async (payload) => {
      return handleAuthRequest(() => appleLoginApi(payload));
    },
    [handleAuthRequest]
  );

  const signup = useCallback(
    async (payload) => {
      return handleAuthRequest(() => signupUser(payload));
    },
    [handleAuthRequest]
  );

    const verifyEmail = useCallback(
    async (payload) => {
      return handleAuthRequest(() => verifyEmailApi(payload));
    },
    [handleAuthRequest]
  );

  const resendOtp = useCallback(
    async (payload) => {
      return handleAuthRequest(() => resendOtpApi(payload));
    },
    [handleAuthRequest]
  );

  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    try {
      const { user: profile } = await fetchCurrentUser();
      persistAuth(profile, token);
      return profile;
    } catch (error) {
      if (error?.status === 401) {
        clearAuth();
        return null;
      }
      console.error('Failed to refresh user:', error);
      throw error;
    }
  }, [token, persistAuth, clearAuth]);

  const value = useMemo(
    () => ({
      user,
      token,
      authLoading,
      authError,
      login,
      signup,
      logout,
      initialized,
      verifyEmail,
      resendOtp,
      refreshUser,
      loginWithGoogle,
      loginWithApple,
    }),
    [user, token, authLoading, authError, login, signup, logout, initialized, verifyEmail, resendOtp, refreshUser, loginWithGoogle, loginWithApple]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};


